import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { AccountIcon, Avatar, ChevronIcon, LiveIcon, LogoMark, OverviewIcon, PartnersIcon, SearchIcon, UsersIcon } from '@/components/icons'
import { Sheet } from '@/components/ui/Sheet'
import { AlertsToggle } from '@/features/live/AlertsToggle'
import { TimeLeft } from '@/features/live/Countdown'
import { useLiveAlerts } from '@/features/live/alerts'
import { reasonOf, useLiveQueue } from '@/features/live/live'
import { useTheme, type ThemeChoice } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'

/**
 * The admin shell: the job seeker app's frame (a left sidebar from md up, a
 * bottom tab bar on phones, a slim top bar) around the admin's places.
 *
 * What is its own: the live queue is always in view. The nav carries its
 * count, and whenever a check is waiting a bar under the top bar says whose,
 * how long is left, and opens it in one tap from any page.
 */
const NAV = [
  { to: '/', label: 'Overview', end: true, icon: OverviewIcon },
  { to: '/live', label: 'Live checks', short: 'Live', end: false, icon: LiveIcon, live: true },
  { to: '/users', label: 'Users', end: false, icon: UsersIcon },
  { to: '/partners', label: 'Partners', end: false, icon: PartnersIcon },
] as const

export function AdminLayout() {
  const admin = useAuthStore((state) => state.admin)
  const navigate = useNavigate()
  const location = useLocation()
  const queue = useLiveQueue()
  const [account, setAccount] = useState(false)
  const waiting = queue.items.length
  useLiveAlerts(queue.items, (handoffId) => navigate(`/live/${encodeURIComponent(handoffId)}`))

  // `/` anywhere: jump to the user search.
  const search = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      event.preventDefault()
      search.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const onSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const term = (new FormData(event.currentTarget).get('q') as string | null)?.trim() ?? ''
    navigate(term ? `/users?search=${encodeURIComponent(term)}` : '/users')
    search.current?.blur()
  }

  const name = admin?.fullName || admin?.email
  const onLive = location.pathname.startsWith('/live')
  const urgent = queue.items[0]

  return (
    <div className="min-h-dvh bg-canvas md:flex">
      {/* ---- Sidebar (md+) ---- */}
      <aside className="sticky top-0 hidden h-dvh w-[68px] shrink-0 flex-col border-r border-line bg-surface md:flex lg:w-[232px]">
        <Link to="/" className="flex h-14 items-center gap-2.5 px-3.5 lg:px-5" aria-label="JobSlide admin home">
          <LogoMark />
          <span className="hidden min-w-0 lg:block">
            <span className="block text-[16px] font-semibold leading-none tracking-tight text-ink">JobSlide</span>
            <span className="mt-1 block text-[10.5px] font-semibold uppercase leading-none tracking-[0.12em] text-ink-muted">Admin</span>
          </span>
        </Link>

        <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 px-2.5 pt-2 lg:px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  'group relative flex h-10 items-center gap-3 rounded-field px-2.5 text-[14px] font-medium transition-colors',
                  isActive ? 'bg-accent-soft text-ink' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span aria-hidden="true" className="absolute -left-2.5 top-2 h-6 w-[3px] rounded-r bg-brand lg:-left-3" />}
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  <span className="hidden min-w-0 flex-1 truncate lg:inline">{item.label}</span>
                  {'live' in item && waiting > 0 && <CountBadge count={waiting} className="absolute right-1.5 top-1.5 lg:static" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-2.5 lg:p-3">
          <button
            type="button"
            onClick={() => setAccount(true)}
            className="flex w-full items-center gap-3 rounded-field px-1.5 py-1.5 text-left transition-colors hover:bg-surface-muted"
            aria-label="Account"
          >
            <Avatar name={name} />
            <span className="hidden min-w-0 flex-1 lg:block">
              <span className="block truncate text-[13px] font-medium text-ink">{name}</span>
              <span className="block text-[11.5px] text-ink-muted">Administrator</span>
            </span>
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* ---- Top bar ---- */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2.5 border-b border-line bg-surface/90 px-4 backdrop-blur-md sm:px-6 lg:px-8" style={{ paddingTop: 'var(--safe-top)' }}>
          <Link to="/" className="flex items-center gap-2 md:hidden" aria-label="JobSlide admin home">
            <LogoMark className="h-7 w-7" />
          </Link>
          <form onSubmit={onSearch} role="search" className="relative min-w-0 flex-1 md:max-w-[420px]">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
            <input
              ref={search}
              name="q"
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              placeholder="Find a user by name or email"
              aria-label="Find a user"
              className="h-9 w-full rounded-field border border-line bg-surface-muted pl-9 pr-9 text-[14px] text-ink placeholder:text-ink-faint transition focus:border-brand/60 focus:bg-surface focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
            <kbd className="kbd pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 [@media(hover:hover)]:block">/</kbd>
          </form>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <AlertsToggle compact />
            <button type="button" onClick={() => setAccount(true)} aria-label="Account" className="md:hidden">
              <Avatar name={name} size="sm" />
            </button>
          </div>
        </header>

        {/* ---- A check is waiting: say so on every page but its own ---- */}
        {urgent && !onLive && (
          <Link
            to={`/live/${encodeURIComponent(urgent.handoffId)}`}
            className="flex items-center gap-3 border-b border-warning/30 bg-warning/[0.09] px-4 py-2.5 transition-colors hover:bg-warning/[0.14] sm:px-6 lg:px-8"
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
            </span>
            <p className="min-w-0 flex-1 truncate text-[13.5px] text-ink">
              <span className="font-semibold">{waiting === 1 ? `${reasonOf(urgent.reason).label} waiting` : `${waiting} checks waiting`}</span>
              <span className="text-ink-soft"> · {urgent.user?.fullName || urgent.user?.email || 'a user'}</span>
              {urgent.companyName && <span className="hidden text-ink-muted sm:inline"> · {urgent.companyName}</span>}
            </p>
            <TimeLeft remaining={urgent.remainingSeconds} waited={urgent.waitedSeconds} />
            <span className="hidden h-8 items-center rounded-[8px] bg-ink px-3 text-[12.5px] font-medium text-oncolor sm:inline-flex">Solve now</span>
            <ChevronIcon className="h-4 w-4 text-ink-muted sm:hidden" />
          </Link>
        )}

        <main className="page-pad-bottom mx-auto w-full min-w-0 max-w-[1280px] flex-1 animate-fade-in px-4 pt-5 sm:px-6 sm:pt-6 lg:px-8 2xl:max-w-[1480px]">
          <Outlet />
        </main>
      </div>

      {/* ---- Bottom tab bar (phones) ---- */}
      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur-md md:hidden" style={{ paddingBottom: 'var(--safe-bottom)' }}>
        <ul className="grid h-[var(--tabbar-h)] grid-cols-5">
          {NAV.map((item) => (
            <li key={item.to} className="min-w-0">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) => cn('relative flex h-full flex-col items-center justify-center gap-1 text-[10.5px] font-medium transition-colors', isActive ? 'text-ink' : 'text-ink-muted')}
              >
                {({ isActive }) => (
                  <>
                    <span className={cn('grid h-7 w-12 place-items-center rounded-pill transition-colors', isActive && 'bg-accent-soft')}>
                      <item.icon className="h-[20px] w-[20px]" />
                    </span>
                    <span className="truncate">{'short' in item ? item.short : item.label}</span>
                    {'live' in item && waiting > 0 && <CountBadge count={waiting} className="absolute left-1/2 top-1 ml-1.5" />}
                  </>
                )}
              </NavLink>
            </li>
          ))}
          <li className="min-w-0">
            <button type="button" onClick={() => setAccount(true)} className="flex h-full w-full flex-col items-center justify-center gap-1 text-[10.5px] font-medium text-ink-muted">
              <span className="grid h-7 w-12 place-items-center">
                <AccountIcon className="h-[20px] w-[20px]" />
              </span>
              Account
            </button>
          </li>
        </ul>
      </nav>

      <AccountSheet open={account} onClose={() => setAccount(false)} />
    </div>
  )
}

function CountBadge({ count, className }: { count: number; className?: string }) {
  return (
    <span
      aria-label={`${count} waiting`}
      className={cn('num grid h-[16px] min-w-[16px] place-items-center rounded-pill bg-warning px-1 text-[10px] font-semibold leading-none text-oncolor', className)}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const admin = useAuthStore((state) => state.admin)
  const logout = useAuthStore((state) => state.logout)
  const [choice, , setTheme] = useTheme()
  const options: Array<{ value: ThemeChoice; label: string }> = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ]
  return (
    <Sheet open={open} onClose={onClose} side="bottom" size="sm">
      <div className="flex items-center gap-3 pb-4">
        <Avatar name={admin?.fullName || admin?.email} size="lg" />
        <div className="min-w-0 flex-1">
          {admin?.fullName && <p className="truncate text-[15px] font-semibold text-ink">{admin.fullName}</p>}
          <p className="truncate text-[13px] text-ink-muted">{admin?.email}</p>
          <p className="mt-1 inline-flex rounded-pill bg-warning/14 px-2 py-0.5 text-[11px] font-medium text-warning">Administrator</p>
        </div>
      </div>

      <div className="pb-4">
        <p className="label-eyebrow mb-2">Theme</p>
        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-1 rounded-field bg-surface-muted p-1">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={choice === option.value}
              onClick={() => setTheme(option.value)}
              className={cn('h-9 rounded-[8px] text-[13px] font-medium transition-colors', choice === option.value ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink')}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-field border border-line px-4 py-3">
        <div className="min-w-0">
          <p className="text-[14px] font-medium text-ink">Alerts</p>
          <p className="text-[12.5px] text-ink-muted">A tone and a notification when a check arrives.</p>
        </div>
        <AlertsToggle />
      </div>

      <button type="button" onClick={() => void logout()} className="mt-4 h-11 w-full rounded-field text-[14px] font-medium text-danger transition-colors hover:bg-danger/5">
        Log out
      </button>
    </Sheet>
  )
}
